import { observer } from 'mobx-react-lite';
import { type ComponentProps, useCallback, useState } from 'react';

import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import type { SubscriptionPlan } from 'src/models/subscription-plan/subscription-plan';
import {
  AnalyticsPaymentMethod,
  handleCheckoutOpenRequestWithPaymentSystem,
} from 'src/utils/payment/payment-analytics';

import { useAnalytics } from '@features/Analytics';
import { createAppLovinSubscriptionPlanPayload } from '@features/Analytics/utils';
import { useFeature } from '@features/FeatureToggle';

import CreditCard from '../../../../assets/icons/credit-card.svg';
import usePaymentSystem, {
  type PaymentSystem,
} from '../../../../utils/payment/use-payment-system';
import PaymentButton from '../payment-button/payment-button';

const paymentSystem: PaymentSystem = 'BRAINTREE';

const CardPaymentButton = observer(
  ({
    subscriptionPlan,
    discount = false,
    ...props
  }: {
    subscriptionPlan: SubscriptionPlan;
    discount?: boolean;
  } & Omit<ComponentProps<typeof PaymentButton>, 'children'>) => {
    const {
      authStore: { user },
    } = useStores();

    const {
      trackFacebookEvent,
      trackGoogleEvent,
      trackPinterestEvent,
      trackAppLovin,
    } = useAnalytics();
    const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');

    const [isLoading, setIsLoading] = useState(false);

    const { contentNode, openCheckout } = usePaymentSystem({
      subscriptionPlan,
      paymentSystem,
      setIsLoading,
    });

    const handleClick = useCallback(async () => {
      if (paymentSystem != null) {
        handleCheckoutOpenRequestWithPaymentSystem({
          subscriptionPlan,
          paymentMethod: AnalyticsPaymentMethod.CARD,
          paymentSystem,
          email: user!.email,
        });

        !fbPixelSept2025Feature &&
          trackFacebookEvent({
            eventName: 'AddPaymentInfo',
            customData: {
              paymentMethod: 'card',
              paymentSystem: 'braintree',
            },
            options: {
              email: user?.email,
            },
          });
        trackPinterestEvent({ eventName: 'AddToCart' });
        trackAppLovin({
          eventName: 'add_payment_info',
          options: createAppLovinSubscriptionPlanPayload({
            subscriptionPlan,
            paymentMethod: 'card',
            paymentSystem: 'braintree',
            userEmail: user?.email,
            userId: user?.serverId,
          }),
        });

        if (discount) {
          trackGoogleEvent({ eventName: 'offer_onetime_tapped' });
        }
      }

      await openCheckout();
    }, [
      discount,
      fbPixelSept2025Feature,
      openCheckout,
      subscriptionPlan,
      trackAppLovin,
      trackFacebookEvent,
      trackGoogleEvent,
      trackPinterestEvent,
      user,
    ]);

    return (
      <>
        <PaymentButton
          data-testid="pay-via-card"
          IconComponent={!discount && CreditCard}
          handleClick={handleClick}
          showLoadingIndicator={isLoading}
          {...props}
        >
          {discount ? 'GET MY DISCOUNT' : 'Debit / credit card'}
        </PaymentButton>
        {contentNode}
      </>
    );
  },
);

export default CardPaymentButton;
